/**
 * Auth Page - Login / Register
 * Email + Password and Google authentication with Firebase
 */

import { storage } from '../utils/storage.js';
import { firebaseAuth } from '../services/firebase.js';
import { authApi, expensesApi } from '../services/api.js';

export function renderAuth(container) {
  container.innerHTML = `
    <div class="auth-page">
      <div class="auth-container">
        <div class="auth-card animate-slideUp">
          <div class="auth-header">
            <div class="auth-logo">
              <a href="/" data-link>
                <svg width="48" height="48" viewBox="0 0 100 100" fill="none">
                  <defs>
                    <linearGradient id="authLogoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style="stop-color:#1a5276"/>
                      <stop offset="100%" style="stop-color:#0f3049"/>
                    </linearGradient>
                  </defs>
                  <rect width="100" height="100" rx="16" fill="url(#authLogoGrad)"/>
                  <path d="M50 20L20 45V80H40V60H60V80H80V45L50 20Z" fill="white"/>
                  <circle cx="50" cy="48" r="7" fill="url(#authLogoGrad)"/>
                </svg>
              </a>
            </div>
            <h1 class="auth-title" id="authTitle">Bienvenido de nuevo</h1>
            <p class="auth-subtitle" id="authSubtitle">
              Inicia sesión para continuar
            </p>
          </div>
          
          <!-- Google Login Button -->
          <button class="btn btn-google" id="googleLoginBtn">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Iniciar sesión con Google
          </button>
          
          <div class="auth-divider">
            <span>o con email y contraseña</span>
          </div>
          
          <!-- Auth Form -->
          <form id="authForm">
            <div class="form-group">
              <label class="form-label" for="email">Email</label>
              <input 
                type="email" 
                id="email" 
                class="form-input" 
                placeholder="tu@email.com"
                required
                autocomplete="email"
              />
            </div>
            
            <div class="form-group">
              <label class="form-label" for="password">Contraseña</label>
              <input 
                type="password" 
                id="password" 
                class="form-input" 
                placeholder="••••••••"
                required
                minlength="6"
                autocomplete="current-password"
              />
            </div>
            
            <div class="form-group hidden" id="confirmPasswordGroup">
              <label class="form-label" for="confirmPassword">Confirmar contraseña</label>
              <input 
                type="password" 
                id="confirmPassword" 
                class="form-input" 
                placeholder="••••••••"
                autocomplete="new-password"
              />
            </div>
            
            <div id="authError" class="alert alert-error hidden" style="margin-bottom: 16px;">
              <i data-feather="alert-circle"></i>
              <span id="authErrorText"></span>
            </div>
            
            <button type="submit" class="btn btn-primary btn-lg w-full" id="authSubmitBtn">
              <span id="authSubmitText">Iniciar sesión</span>
              <div class="spinner hidden" id="authSpinner"></div>
            </button>
          </form>
          
          <div class="auth-forgot" id="forgotPasswordSection">
            <a href="#" id="forgotPasswordLink">¿Olvidaste tu contraseña?</a>
          </div>
          
          <div class="auth-footer">
            <span id="authToggleText">¿No tienes cuenta?</span>
            <a href="#" id="authToggleLink">Regístrate</a>
          </div>
          
          <div class="auth-legal">
            <i data-feather="shield" style="width: 14px; height: 14px; margin-right: 4px;"></i>
            Al continuar, aceptas nuestros <a href="/terminos" data-link style="color: var(--primary-600);">Términos</a> 
            y <a href="/privacidad" data-link style="color: var(--primary-600);">Política de Privacidad</a>. 
            <br><br>
            <strong>⚠️ Este servicio no constituye asesoramiento financiero.</strong>
          </div>
        </div>
      </div>
    </div>
  `;

  initAuthForm();
}

function initAuthForm() {
  // Start in LOGIN mode by default
  let isLoginMode = true;
  // Track if this is a new registration (to force onboarding)
  let isNewRegistration = false;

  const authForm = document.getElementById('authForm');
  const authTitle = document.getElementById('authTitle');
  const authSubtitle = document.getElementById('authSubtitle');
  const authSubmitText = document.getElementById('authSubmitText');
  const authToggleText = document.getElementById('authToggleText');
  const authToggleLink = document.getElementById('authToggleLink');
  const confirmPasswordGroup = document.getElementById('confirmPasswordGroup');
  const confirmPasswordInput = document.getElementById('confirmPassword');
  const authError = document.getElementById('authError');
  const authErrorText = document.getElementById('authErrorText');
  const authSpinner = document.getElementById('authSpinner');
  const googleLoginBtn = document.getElementById('googleLoginBtn');
  const forgotPasswordSection = document.getElementById('forgotPasswordSection');
  const forgotPasswordLink = document.getElementById('forgotPasswordLink');

  function updateFormMode() {
    if (isLoginMode) {
      authTitle.textContent = 'Bienvenido de nuevo';
      authSubtitle.textContent = 'Inicia sesión para continuar';
      authSubmitText.textContent = 'Iniciar sesión';
      authToggleText.textContent = '¿No tienes cuenta?';
      authToggleLink.textContent = 'Regístrate';
      confirmPasswordGroup.classList.add('hidden');
      confirmPasswordInput.removeAttribute('required');
      forgotPasswordSection.classList.remove('hidden');
    } else {
      authTitle.textContent = 'Crear cuenta';
      authSubtitle.textContent = 'Regístrate gratis y accede a toda la información';
      authSubmitText.textContent = 'Crear cuenta';
      authToggleText.textContent = '¿Ya tienes cuenta?';
      authToggleLink.textContent = 'Inicia sesión';
      confirmPasswordGroup.classList.remove('hidden');
      confirmPasswordInput.setAttribute('required', 'true');
      forgotPasswordSection.classList.add('hidden');
    }
    authError.classList.add('hidden');
  }

  function showError(message) {
    authErrorText.textContent = message;
    authError.classList.remove('hidden');
    authError.classList.remove('alert-success');
    authError.classList.add('alert-error');
  }

  function showSuccess(message) {
    authError.classList.remove('hidden');
    authError.classList.remove('alert-error');
    authError.classList.add('alert-success');
    authErrorText.textContent = message;
  }

  function setLoading(loading) {
    const submitBtn = document.getElementById('authSubmitBtn');
    if (loading) {
      authSpinner.classList.remove('hidden');
      authSubmitText.style.opacity = '0';
      submitBtn.disabled = true;
      googleLoginBtn.disabled = true;
    } else {
      authSpinner.classList.add('hidden');
      authSubmitText.style.opacity = '1';
      submitBtn.disabled = false;
      googleLoginBtn.disabled = false;
    }
  }

  /**
   * Handle successful authentication
   * @param {Object} firebaseResult - Firebase auth result
   * @param {boolean} forceOnboarding - If true, always go to onboarding (for new registrations)
   */
  async function handleAuthSuccess(firebaseResult, forceOnboarding = false) {
    try {
      // Send Firebase token to backend
      const response = await authApi.loginWithFirebase(firebaseResult.idToken);

      // Store user info
      storage.setUser({
        id: response.user.id,
        email: response.user.email,
        createdAt: new Date().toISOString()
      });

      // Store JWT token - CRITICAL for all subsequent API calls
      if (response.accessToken) {
        storage.setToken(response.accessToken);
      }

      // Store profile if exists
      if (response.user.profile) {
        storage.setProfile(response.user.profile);

        // Restore home goal from backend profile
        if (response.user.profile.homeGoalPrice || response.user.profile.homeGoalMortgagePct) {
          storage.setHomeGoal({
            price: response.user.profile.homeGoalPrice || 200000,
            mortgagePercentage: response.user.profile.homeGoalMortgagePct || 80
          });
        }
      }

      // Load expenses from backend response (already included in login response)
      try {
        const backendExpenses = response.user.expenses;
        if (backendExpenses && (backendExpenses.rent || backendExpenses.fixedExpenses || backendExpenses.variableExpenses)) {
          storage.setExpenses({
            rent: backendExpenses.rent || 0,
            fixed: backendExpenses.fixedExpenses || 0,
            variable: backendExpenses.variableExpenses || 0
          });
        }
      } catch (e) {
        console.warn('Could not restore expenses:', e);
      }

      // Navigate to appropriate page
      // If it's a new registration, ALWAYS go to onboarding
      if (forceOnboarding) {
        // Clear any existing profile for new registrations
        storage.setProfile(null);
        window.router.navigate('/onboarding');
      } else if (response.user.hasProfile) {
        window.router.navigate('/dashboard');
      } else {
        window.router.navigate('/onboarding');
      }
    } catch (error) {
      console.error('Backend auth error:', error);
      // If backend is not available, continue with local-only mode
      storage.setUser({
        id: 'local_' + Date.now(),
        email: firebaseResult.user.email,
        createdAt: new Date().toISOString()
      });

      // If it's a new registration, ALWAYS go to onboarding
      if (forceOnboarding) {
        // Clear any existing profile for new registrations
        storage.setProfile(null);
        window.router.navigate('/onboarding');
      } else if (storage.getProfile()) {
        window.router.navigate('/dashboard');
      } else {
        window.router.navigate('/onboarding');
      }
    }
  }

  // Toggle between login and register
  authToggleLink.addEventListener('click', (e) => {
    e.preventDefault();
    isLoginMode = !isLoginMode;
    updateFormMode();
  });

  // Forgot password
  forgotPasswordLink.addEventListener('click', async (e) => {
    e.preventDefault();
    const email = document.getElementById('email').value;

    if (!email) {
      showError('Introduce tu email para recuperar tu contraseña');
      return;
    }

    setLoading(true);
    const result = await firebaseAuth.sendPasswordReset(email);
    setLoading(false);

    if (result.success) {
      showSuccess('Te hemos enviado un email para restablecer tu contraseña');
    } else {
      showError(result.error);
    }
  });

  // Form submission
  authForm.addEventListener('submit', async (e) => {
    e.preventDefault();

    const email = document.getElementById('email').value;
    const password = document.getElementById('password').value;

    // Validation for registration mode
    if (!isLoginMode) {
      const confirmPassword = document.getElementById('confirmPassword').value;
      if (password !== confirmPassword) {
        showError('Las contraseñas no coinciden');
        return;
      }
    }

    setLoading(true);
    authError.classList.add('hidden');

    try {
      let result;

      if (isLoginMode) {
        // LOGIN - existing user
        isNewRegistration = false;
        result = await firebaseAuth.loginWithEmail(email, password);
      } else {
        // REGISTER - new user, ALWAYS go to onboarding
        isNewRegistration = true;
        result = await firebaseAuth.registerWithEmail(email, password);
      }

      if (result.success) {
        // Pass isNewRegistration to force onboarding for new users
        await handleAuthSuccess(result, isNewRegistration);
      } else {
        showError(result.error);
      }
    } catch (error) {
      console.error('Auth error:', error);
      showError('Ha ocurrido un error. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  });

  // Google login - Check if user is new or existing
  googleLoginBtn.addEventListener('click', async () => {
    setLoading(true);
    authError.classList.add('hidden');

    try {
      const result = await firebaseAuth.loginWithGoogle();

      if (result.success) {
        // Check if user is new (no profile) - Google can be used for both login and register
        // We check if the user was created in the last 10 seconds
        const creationTime = result.user.metadata?.creationTime;
        const lastSignInTime = result.user.metadata?.lastSignInTime;

        // If creation time equals last sign in time (within 10 seconds), it's a new user
        const isNewGoogleUser = creationTime && lastSignInTime &&
          Math.abs(new Date(creationTime) - new Date(lastSignInTime)) < 10000;

        await handleAuthSuccess(result, isNewGoogleUser);
      } else {
        showError(result.error);
      }
    } catch (error) {
      console.error('Google auth error:', error);
      showError('Ha ocurrido un error. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  });
}
